import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import { CheckboxGroupOption } from '../button-checkbox-group/button-checkbox-group.component';

type Value = Array<CheckboxGroupOption> | null;

@Component({
  selector: 'ui-platform-checkbox-group',
  templateUrl: './checkbox-group.component.html',
  styleUrl: './checkbox-group.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzCheckboxModule,
    TranslocoPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPlatformCheckboxGroupComponent),
      multi: true
    }
  ]
})
export class UiPlatformCheckboxGroupComponent implements ControlValueAccessor {
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  @Input() label = '';
  @Input({ required: true }) checkboxGroupOptions: Array<CheckboxGroupOption> = [];
  disabled = false;

  #internalValue: Value = null;

  syncExternalValue: (value: Value) => void = () => {};
  syncExternalTouched: () => void = () => {};

  onModelChange(value: Value): void {
    const valueToStore =
      value?.map(option => {
        return {
          value: option.value,
          checked: option.checked
        };
      }) ?? null;
    this.syncExternalValue(valueToStore);
    this.syncExternalTouched();
  }

  writeValue(value: Value): void {
    this.#internalValue = value;
    this.#changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: Value) => void): void {
    this.syncExternalValue = (value: Value) => {
      fn(value);
    };
  }

  registerOnTouched(fn: () => void): void {
    this.syncExternalTouched = () => {
      fn();
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.#changeDetectorRef.detectChanges();
  }
}
